import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import { connectToSocket } from "../../services/socket";
import { useMenuStyles } from "../../components/menuStyles";

// Base compartilhada das telas do CRM (Funis, Contatos, Segmentos, Campanhas).
// Tokens = os do app (App.js theme.app) — nada de paleta própria.
export const T = {
  bg: "#F6F7F9",
  panel: "#FFFFFF",
  line: "#E6E9ED",
  lineSoft: "#EEF0F3",
  hover: "#FAFBFC",
  ink: "#1B222A",
  ink2: "#5B6470",
  ink3: "#929AA4",
  dark: "#2C353D",
  lime: "#CEFF00",
  limeSoft: "#F0F9D6",
  limeInk: "#4D6B00",
  ok: "#1E9E62",
  okSoft: "#E6F5EC",
  warn: "#C77A12",
  warnSoft: "#FBF0DF",
  danger: "#C0453B",
  dangerSoft: "#F8E5E3",
  info: "#2F6FB0",
  infoSoft: "#E5EEF8",
  alt: "#6A52C4",
  altSoft: "#EFEAFB",
  human: "#E3EEFB",
  humanInk: "#1F5FA6",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};

export const STAGE_COLORS = ["#929AA4", "#2F6FB0", "#86C240", "#C77A12", "#1E9E62", "#C0453B", "#6A52C4", "#0B7285"];

// estilos de página/cabeçalho comuns (mesmo desenho de Tabelas/Coletores)
export const usePageStyles = makeStyles(theme => ({
  page: { background: T.bg, minHeight: "100%" },
  wrap: { maxWidth: 1180, margin: "0 auto", padding: theme.spacing(4, 3) },
  header: { display: "flex", alignItems: "flex-start", gap: 14, marginBottom: theme.spacing(3), flexWrap: "wrap" },
  headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: T.altSoft, color: T.alt, marginTop: 2 },
  title: { fontFamily: theme.app.heading, fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.2 },
  subtitle: { fontSize: 13.5, color: T.ink3, marginTop: 4, maxWidth: 680 },
  grow: { flex: 1 },
  box: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, padding: theme.spacing(2) },
  boxTitle: { fontFamily: theme.app.heading, fontSize: 15, fontWeight: 600, color: T.ink, margin: 0 },
  label: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.ink3 },
  hint: { fontSize: 12, color: T.ink2 },
  empty: { textAlign: "center", color: T.ink3, padding: theme.spacing(8, 2), fontSize: 14 },
  btnPri: { background: T.lime, color: T.dark, fontWeight: 600, textTransform: "none", borderRadius: 8, boxShadow: "none", "&:hover": { background: "#bce600", boxShadow: "none" } },
  btnDark: { background: T.dark, color: "#fff", fontWeight: 600, textTransform: "none", borderRadius: 8, "&:hover": { background: "#1f262c" } },
  btn: { textTransform: "none", borderRadius: 8, borderColor: T.line, color: T.ink, background: "#fff" },
  tabular: { fontVariantNumeric: "tabular-nums" },
}));

export const chipStyle = kind => {
  const map = {
    collector: [T.altSoft, T.alt],
    whatsapp: [T.okSoft, T.ok],
    ai: [T.limeSoft, T.limeInk],
    human: [T.human, T.humanInk],
    warn: [T.warnSoft, T.warn],
    danger: [T.dangerSoft, T.danger],
    info: [T.infoSoft, T.info],
    ok: [T.okSoft, T.ok],
  };
  const [bg, fg] = map[kind] || [T.lineSoft, T.ink2];
  return { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, borderRadius: 999, padding: "1px 8px", background: bg, color: fg, whiteSpace: "nowrap", fontWeight: 500, lineHeight: 1.6 };
};

export const Chip = ({ kind, children, title, style }) => (
  <span title={title} style={{ ...chipStyle(kind), ...(style || {}) }}>
    {children}
  </span>
);

export const Dot = ({ color, size = 8 }) => <span style={{ width: size, height: size, borderRadius: "50%", background: color || T.ink3, display: "inline-block", flexShrink: 0 }} />;

// origem do contato → chip (coletor/whatsapp/import/manual/agente…)
export const sourceKind = s => {
  const k = (s && s.kind) || "";
  if (k === "collector") return "collector";
  if (k === "whatsapp") return "whatsapp";
  if (k === "agent") return "ai";
  if (k === "campaign") return "info";
  return null;
};
export const sourceLabel = s => {
  if (!s) return "";
  if (s.label) return s.label;
  return { collector: "Coletor", whatsapp: "WhatsApp", import: "Importação", manual: "Manual", agent: "Agente de IA", api: "API", flow: "Automação" }[s.kind] || s.kind;
};

// ---------- formatação ----------
const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const BRL2 = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
export const money = (v, cents) => (v == null || v === "" || Number.isNaN(Number(v)) ? "" : (cents ? BRL2 : BRL).format(Number(v)));

export const fmtPhone = p => {
  const d = String(p || "").replace(/\D/g, "");
  if (d.startsWith("55") && (d.length === 12 || d.length === 13)) {
    const ddd = d.slice(2, 4);
    const n = d.slice(4);
    return `+55 ${ddd} ${n.length === 9 ? `${n.slice(0, 5)}-${n.slice(5)}` : `${n.slice(0, 4)}-${n.slice(4)}`}`;
  }
  return d ? `+${d}` : "";
};

export const fmtDate = v => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export const timeAgo = v => {
  if (!v) return "";
  const s = Math.max(0, (Date.now() - new Date(v).getTime()) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86400);
  if (d === 1) return "ontem";
  if (d < 30) return `há ${d} dias`;
  return new Date(v).toLocaleDateString("pt-BR");
};

export const daysSince = v => (v ? (Date.now() - new Date(v).getTime()) / 86400000 : 0);

// valor de campo extra formatado pelo tipo do dicionário
export const fmtField = (type, v) => {
  if (v == null || v === "") return "—";
  if (type === "moeda") return money(v, true);
  if (type === "numero") return Number(v).toLocaleString("pt-BR");
  if (type === "data") {
    const d = new Date(String(v).replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("pt-BR");
  }
  if (type === "booleano") return /^(true|sim|1)$/i.test(String(v)) ? "Sim" : "Não";
  if (type === "telefone") return fmtPhone(v);
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

export const FIELD_TYPES = [
  { value: "texto", label: "Texto" },
  { value: "numero", label: "Número" },
  { value: "moeda", label: "Moeda (R$)" },
  { value: "data", label: "Data" },
  { value: "booleano", label: "Sim/Não" },
  { value: "telefone", label: "Telefone" },
  { value: "email", label: "E-mail" },
  { value: "opcao", label: "Opção" },
];

// ---------- timeline ----------
// ícone (letra), cor e título de cada tipo de atividade
export const ACTIVITY = {
  "contato.criado": { ic: "C", color: T.alt, title: a => `Contato criado${a.payload && a.payload.origem && a.payload.origem.label ? ` · ${a.payload.origem.label}` : ""}` },
  "campo.alterado": { ic: "✎", color: T.ink3, title: a => `Campos atualizados${actorSuffix(a)}` },
  "etapa.alterada": {
    ic: "→",
    color: T.dark,
    title: a => {
      const p = a.payload || {};
      return p.de ? `Etapa: ${p.de} → ${p.para}` : `Entrou no funil ${p.funil || ""} · ${p.para}`;
    },
  },
  "mensagem.recebida": { ic: "W", color: T.ok, title: () => "Mensagem recebida" },
  "mensagem.enviada": { ic: "W", color: T.ok, title: () => "Mensagem enviada" },
  "ia.resultado": { ic: "IA", color: "#7FA300", title: () => "A IA encerrou a conversa com um resultado" },
  "campanha.enviada": { ic: "✉", color: T.info, title: a => `Campanha enviada · ${(a.payload && a.payload.campanha) || ""}` },
  "campanha.respondida": { ic: "↩", color: T.info, title: a => `Respondeu à campanha · ${(a.payload && a.payload.campanha) || ""}` },
  nota: { ic: "N", color: T.warn, title: a => `Nota${actorSuffix(a)}` },
  tag: { ic: "#", color: T.ink2, title: () => "Tags atualizadas" },
  optout: { ic: "⦸", color: T.danger, title: a => (a.payload && a.payload.bloqueado === false ? "Voltou a aceitar mensagens" : "Pediu para não receber mensagens") },
  ligacao: {
    ic: "☎",
    color: T.info,
    title: a => {
      const p = a.payload || {};
      if (p.atendeu === false) return "Ligação · não atendeu";
      const d = Number(p.duracaoSeg) || 0;
      return `Ligação${d ? ` · ${Math.floor(d / 60)}m${String(d % 60).padStart(2, "0")}s` : ""}${p.objetivoAtingido ? " · objetivo atingido" : ""}`;
    },
  },
};

const ACTOR_LABEL = { user: "você", agent: "Agente de IA", rule: "regra automática", automation: "automação", collector: "coletor", campaign: "campanha", whatsapp: "WhatsApp", import: "importação", system: "sistema" };
export const actorText = actor => {
  if (!actor) return "";
  if (actor.label) return actor.label;
  return ACTOR_LABEL[actor.kind] || actor.kind || "";
};
const actorSuffix = a => {
  const t = actorText(a.actor);
  return t ? ` · ${t}` : "";
};

// ---------- socket ----------
// assina o evento "crm.update" (sala "crm"), por evento — sem polling. O handler
// mais recente é usado sem reconectar a cada render.
export const useCrmSocket = handler => {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const socket = connectToSocket();
    socket.emit("joinRoom", "crm");
    const on = p => ref.current && ref.current(p || {});
    const onConnect = () => ref.current && ref.current({ kind: "reconnect" });
    socket.on("crm.update", on);
    socket.on("connect", onConnect);
    return () => {
      socket.off("crm.update", on);
      socket.off("connect", onConnect);
      socket.emit("leaveRoom", "crm");
      socket.disconnect();
    };
  }, []);
};

// debounce simples p/ recarregar após rajada de eventos
export const useDebounced = (fn, ms = 400) => {
  const t = useRef(null);
  const f = useRef(fn);
  f.current = fn;
  useEffect(() => () => clearTimeout(t.current), []);
  return (...args) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => f.current(...args), ms);
  };
};

// ---------- mapeamento de colunas (coletor / importação) ----------
// O que cada coluna vira no CRM: identidade (telefone/e-mail/nome/id externo),
// campo extra (com tipo) ou ignorar.
export const ROLE_OPTIONS = [
  { value: "phone", label: "Telefone (identidade)" },
  { value: "email", label: "E-mail (identidade)" },
  { value: "name", label: "Nome" },
  { value: "externalId", label: "Id externo (evita duplicar)" },
  { value: "extra", label: "Campo extra" },
  { value: "ignore", label: "Ignorar" },
];

// detected/mapping {phone,email,name,externalId,extras} → { coluna: {role, type, label} }
export const mappingToRoles = (columns, m) => {
  const out = {};
  (columns || []).forEach(col => {
    let role = "extra";
    if (m && m.phone === col) role = "phone";
    else if (m && m.email === col) role = "email";
    else if (m && m.name === col) role = "name";
    else if (m && m.externalId === col) role = "externalId";
    const ex = (m && m.extras && m.extras[col]) || {};
    if (ex.ignore) role = "ignore";
    out[col] = { role, type: ex.type || "texto", label: ex.label || col, key: ex.key };
  });
  return out;
};

export const rolesToMapping = roles => {
  const m = { phone: null, email: null, name: null, externalId: null, extras: {} };
  Object.keys(roles || {}).forEach(col => {
    const r = roles[col];
    if (r.role === "phone" || r.role === "email" || r.role === "name" || r.role === "externalId") {
      if (!m[r.role]) m[r.role] = col;
      else m.extras[col] = { key: r.key, label: r.label, type: r.type };
    } else m.extras[col] = { key: r.key, label: r.label, type: r.type, ignore: r.role === "ignore" };
  });
  return m;
};

const useMapStyles = makeStyles(() => ({
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", fontSize: 10.5, color: T.ink3, fontWeight: 600, padding: "6px 8px", borderBottom: `1px solid ${T.line}`, textTransform: "uppercase", letterSpacing: ".05em" },
  td: { padding: "6px 8px", borderBottom: `1px solid ${T.lineSoft}`, verticalAlign: "middle" },
  col: { fontFamily: T.mono, fontSize: 12 },
  ex: { color: T.ink3, fontFamily: T.mono, fontSize: 11.5, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  why: { fontSize: 10.5, color: T.ok, marginLeft: 6 },
  sel: { minWidth: 190, "& .MuiInputBase-root": { fontSize: 12.5 } },
  type: { minWidth: 120, marginLeft: 6, "& .MuiInputBase-root": { fontSize: 12.5 } },
  wrap: { overflowX: "auto" },
}));

// tabela de confirmação: tudo PRÉ-PREENCHIDO pela detecção; o usuário só confere
export const MappingTable = ({ columns, sample, roles, onChange, why }) => {
  const c = useMapStyles();
  const menu = useMenuStyles();
  const set = (col, patch) => onChange({ ...roles, [col]: { ...roles[col], ...patch } });
  const ex = col => {
    const v = (sample || []).map(r => r && r[col]).find(x => x != null && x !== "");
    return v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
  };
  return (
    <div className={c.wrap}>
      <table className={c.table}>
        <thead>
          <tr>
            <th className={c.th}>Coluna</th>
            <th className={c.th}>Exemplo</th>
            <th className={c.th}>Vira no CRM</th>
          </tr>
        </thead>
        <tbody>
          {(columns || []).map(col => {
            const r = roles[col] || { role: "extra", type: "texto" };
            return (
              <tr key={col}>
                <td className={`${c.td} ${c.col}`}>{col}</td>
                <td className={`${c.td}`}>
                  <div className={c.ex} title={ex(col)}>
                    {ex(col) || "—"}
                  </div>
                </td>
                <td className={c.td}>
                  <TextField select size="small" variant="outlined" className={c.sel} value={r.role} onChange={e => set(col, { role: e.target.value })} SelectProps={{ MenuProps: { classes: { paper: menu.selectPaper } } }}>
                    {ROLE_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {r.role === "extra" && (
                    <TextField select size="small" variant="outlined" className={c.type} value={r.type || "texto"} onChange={e => set(col, { type: e.target.value })} SelectProps={{ MenuProps: { classes: { paper: menu.selectPaper } } }}>
                      {FIELD_TYPES.map(o => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {why && why[col] && r.role !== "extra" && r.role !== "ignore" && <span className={c.why}>✓ {why[col]}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// CSV → linhas (aspas, ; ou , como separador, cabeçalho na 1ª linha)
export const parseCsv = text => {
  const src = String(text || "").replace(/^﻿/, "");
  const firstLine = src.split(/\r?\n/)[0] || "";
  const delim = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ";" : (firstLine.match(/\t/g) || []).length > (firstLine.match(/,/g) || []).length ? "\t" : ",";
  const rows = [];
  let row = [];
  let cell = "";
  let q = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (q) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === delim) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  const clean = rows.filter(r => r.some(x => String(x).trim() !== ""));
  if (!clean.length) return { columns: [], rows: [] };
  const header = clean[0].map((h, i) => String(h || "").trim() || `coluna_${i + 1}`);
  return {
    columns: header,
    rows: clean.slice(1).map(r => {
      const o = {};
      header.forEach((h, i) => {
        o[h] = r[i] != null ? String(r[i]).trim() : "";
      });
      return o;
    }),
  };
};

export const SelectField = ({ label, value, onChange, options, className, helperText, disabled, fullWidth = true, emptyLabel }) => {
  const menu = useMenuStyles();
  return (
    <TextField select label={label} value={value == null ? "" : value} onChange={e => onChange(e.target.value)} variant="outlined" size="small" fullWidth={fullWidth} className={className} helperText={helperText} disabled={disabled} InputLabelProps={{ shrink: true }} InputProps={{ style: { fontSize: 13 } }} SelectProps={{ displayEmpty: true, MenuProps: { classes: { paper: menu.selectPaper } } }}>
      {emptyLabel != null && <MenuItem value="">{emptyLabel}</MenuItem>}
      {(options || []).map(o => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
